import { queryOptions, useQuery } from '@tanstack/react-query';

import { portfolioEndpoints as contractPortfolioEndpoints } from '@d3/contract';

import { api } from '@shared/api';

import { portfolioKeys } from './portfolio.keys';
import { toPortfolioDetail } from './portfolio.mapper';

/**
 * 포트폴리오 상세 기본정보(`GET /portfolios/:id`, @d3/contract). 회사 정보 · 담당자 · KPI · 코멘트.
 * 상세 화면의 여러 구획 · 정보 수정 화면이 같은 key 를 쓴다 — 한 번 받고 구획마다 `select` 로 고른다.
 *
 * 투자 현황(StockOverview)도 같은 응답의 `indicators`(계약 PortfolioIndicators)에서 고른다 —
 * entities/investment 의 usePortfolioStockQuery 가 같은 key 로 받아 select 로 투자 현황 뷰모델을 만든다.
 */
export function portfolioDetailQueryOptions(idx: number) {
  return queryOptions({
    queryKey: portfolioKeys.detail(idx),
    queryFn: ({ signal }) =>
      api.request(contractPortfolioEndpoints.getPortfolio, { params: { id: idx }, signal }),
  });
}

/** 포트폴리오 상세 뷰모델. 화면 구획용(오류는 구획의 QueryBoundary 가 받는다) */
export function usePortfolioQuery(idx: number) {
  return useQuery({
    ...portfolioDetailQueryOptions(idx),
    throwOnError: true,
    select: toPortfolioDetail,
  });
}

/**
 * 투자 현황 전용 — entities/investment 의 usePortfolioStockQuery 가 쓴다. 기본정보와 같은 계약 응답
 * (`GET /portfolios/:id`, 같은 cache key)을 받고, 투자 현황은 응답의 `indicators` 를 select 로 고른다.
 * 요청을 한 번만 보낸다. 펀드 필터는 getPortfolio 가 모든 펀드를 주므로 mapper 에서 걸러낸다.
 */
export function portfolioStockQueryOptions(idx: number) {
  return portfolioDetailQueryOptions(idx);
}

/** 회사 이름표(id · 이름) — 이름 가나다순 */
export interface PortfolioNameRef {
  id: number;
  name: string;
}

/**
 * 회사 목록 경량 조회(`GET /portfolios`, withMembers 없음) → 이름표만, 이름 가나다순(같은 이름은 id 순).
 * S18 담당 포트폴리오 고르기처럼 모든 회사의 이름만 필요한 곳에 쓴다. 계정 관리의 PF 계정 목록(withMembers=true)과 key 가 다르다.
 */
export function usePortfolioNamesQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: portfolioKeys.list({ kind: 'names' }),
    queryFn: ({ signal }) =>
      api.request(contractPortfolioEndpoints.listPortfolios, {
        query: { sort: 'name,asc' },
        signal,
      }),
    select: (data): PortfolioNameRef[] =>
      data.items
        .map((item) => ({ id: item.id, name: item.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ko') || a.id - b.id),
    enabled: options.enabled ?? true,
  });
}
